import { useEffect, useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory, { PaginationProvider }  from "react-bootstrap-table2-paginator";
import { getAuthorities } from "../../../../../../_helpers/Helpers";
import * as actions from "../../../_redux/school-reports/SchoolReportsAction";
import * as uiHelpers from "../SchoolReportsUIHelpers";
import { 
  getSelectRow, 
  getHandlerTableChange,  
  NoRecordsFoundMessage, 
  PleaseWaitMessage, 
  sortCaret
} from "../../../../../../_metronic/_helpers";
import * as columnFormatters from "./column-formatters";
import { Pagination } from "../../../../../../_metronic/_partials/controls";
import { useSchoolReportsUIContext } from "../SchoolReportsUIContext";

export function SchoolReportsTable({ 
  deleteSchoolReports
}) {
  // School Reports UI Context
  const schoolReportsUIContext = useSchoolReportsUIContext();
  const schoolReportsUIProps = useMemo(() => {
    return {
      ids: schoolReportsUIContext.ids,
      setIds: schoolReportsUIContext.setIds,
      queryParams: schoolReportsUIContext.queryParams,
      setQueryParams: schoolReportsUIContext.setQueryParams,
      openEditSchoolReportPage: schoolReportsUIContext.openEditSchoolReportPage
    };
  }, [schoolReportsUIContext]);

  // Getting curret state of School Reports list from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.schoolReports }),
    shallowEqual
  );
  const { totalCount, entities, authorities, listLoading } = currentState;
  
  // Authority Parameters
  const { isEditionAuthorized, isDeletionAuthorized } = getAuthorities(authorities);
  
  // School Reports Redux state
  const dispatch = useDispatch();

  useEffect(() => {
    schoolReportsUIProps.setIds([]);
    dispatch(actions.getSchoolReports(schoolReportsUIProps.queryParams));
    return () => dispatch(actions.cleanSchoolReportsState());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolReportsUIProps.queryParams, dispatch]);

  // Table columns
  const columns = [
    {
      dataField: "name",
      text: "Karne Adı",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "150px"
      }
    },
    {
      dataField: "startDate",
      text: "Başlangıç Tarihi",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "175px"
      }
    },
    {
      dataField: "endDate",
      text: "Bitiş Tarihi",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "150px"
      }
    },
    {
      dataField: "plannedPrintDate",
      text: "Planlanan Basılma Tarihi",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "240px"
      }
    },
    {
      dataField: "reportStatus",
      text: "Durum",
      sort: true,
      sortCaret: sortCaret,
      formatter: columnFormatters.StatusColumnFormatter,
      style: {
        minWidth: "150px"
      }
    },
    {
      dataField: "printDate",
      text: "Basılma Tarihi",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "175px"
      }
    },
    {
      dataField: "description",
      text: "Açıklaması",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "200px"
      }
    }
  ];

  const actionColumn = {
    dataField: "action",
    text: "İşlemler",
    formatter: columnFormatters.ActionsColumnFormatter,
    formatExtraData: {
      isEditionAuthorized,
      isDeletionAuthorized,
      openEditSchoolReportPage: schoolReportsUIProps.openEditSchoolReportPage,
      deleteSchoolReports
    },
    classes: "text-right pr-0",
    headerClasses: "text-right pr-3",
    style: {
      minWidth: "100px",
    }
  }

  if (isEditionAuthorized || isDeletionAuthorized) columns.push(actionColumn);

  // Table pagination properties
  const paginationOptions = {
    custom: true,
    totalSize: totalCount,
    sizePerPageList: uiHelpers.sizePerPageList,
    sizePerPage: schoolReportsUIProps.queryParams.pageSize,
    page: schoolReportsUIProps.queryParams.pageNumber
  };
  
  return (
    <PaginationProvider pagination={paginationFactory(paginationOptions)}>
      {({ paginationProps, paginationTableProps }) => {
        return (
          <Pagination
            isLoading={listLoading}
            paginationProps={paginationProps}
          >
            <BootstrapTable
              wrapperClasses="table-responsive"
              classes="table table-head-custom table-vertical-center overflow-hidden"
              bootstrap4
              bordered={false}
              remote
              keyField="id"
              data={entities ?? []}
              columns={columns}
              defaultSorted={uiHelpers.defaultSorted}
              onTableChange={getHandlerTableChange(
                schoolReportsUIProps.setQueryParams
              )}
              selectRow={getSelectRow({
                entities,
                ids: schoolReportsUIProps.ids,
                setIds: schoolReportsUIProps.setIds,
                hidden: !isDeletionAuthorized
              })}
              {...paginationTableProps}
            >
              <PleaseWaitMessage entities={entities} />
              <NoRecordsFoundMessage entities={entities} />
            </BootstrapTable>
          </Pagination>
        );    
      }}
    </PaginationProvider>  
  );
}